import {
    Page,
    IndexTable,
    Card,
    ChoiceList,
    TextField,
    Select,
    Thumbnail,
    IndexFilters,
    useSetIndexFiltersMode,
    IndexFiltersMode,
    useIndexResourceState,
} from '@shopify/polaris';
import type { IndexFiltersProps, TabProps } from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageIcon, DeleteIcon } from '@shopify/polaris-icons';
import agent from '../../app/api/agent';
import { RealtimeProduct } from '../../app/models/Product';

type SelectOption = { label: string; value: string };

const DEFAULT_YEAR_OPTIONS: SelectOption[] = [{ label: 'All Years', value: '' }];
const DEFAULT_MAKE_OPTIONS: SelectOption[] = [{ label: 'All Makes', value: '' }];
const DEFAULT_MODEL_OPTIONS: SelectOption[] = [{ label: 'All Models', value: '' }];

declare global {
    interface Window {
        shopify?: { toast?: { show?: (msg: string | { message: string; duration?: number }) => void } };
    }
}

const showToast = (msg: string) => window.shopify?.toast?.show?.(msg);

const PAGE_SIZE = 50;

const ProductsRealtimePage: React.FC = () => {
    const navigate = useNavigate();

    // Real-time Shopify data + cursor pagination bookkeeping.
    const [products, setProducts] = useState<RealtimeProduct[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasNext, setHasNext] = useState(false);
    const [nextCursor, setNextCursor] = useState('');
    const [cursorByPage, setCursorByPage] = useState<Record<number, string>>({ 1: '' });
    const [page, setPage] = useState(1);

    // Legacy-like filters. Query goes to API; the rest are local filters to keep UI matching legacy.
    const [queryValue, setQueryValue] = useState<string>('');
    const [hasFitment, setHasFitment] = useState<string[]>([]);
    const [vendor, setVendor] = useState<string>('');
    const [taggedWith, setTaggedWith] = useState<string>('');

    const [selectedYear, setSelectedYear] = useState<string>('');
    const [selectedMake, setSelectedMake] = useState<string>('');
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [mappedOnly, setMappedOnly] = useState(false);

    const [yearOptions, setYearOptions] = useState<SelectOption[]>(DEFAULT_YEAR_OPTIONS);
    const [makeOptions, setMakeOptions] = useState<SelectOption[]>(DEFAULT_MAKE_OPTIONS);
    const [modelOptions, setModelOptions] = useState<SelectOption[]>(DEFAULT_MODEL_OPTIONS);
    const [loadingYears, setLoadingYears] = useState(false);
    const [loadingMakes, setLoadingMakes] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);

    const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);
    const [selectedTab, setSelectedTab] = useState(0);
    const tabs: TabProps[] = [{ id: 'all', content: 'All' }];

    const [sortSelected, setSortSelected] = useState(['title asc']);

    const loadProducts = useCallback(async (targetPage: number, query: string, cursor: string = '') => {
        setLoading(true);
        try {
            const result = await agent.Products.listRealtime(query, cursor, PAGE_SIZE);
            setProducts(result.products);
            setHasNext(result.hasNext);
            setNextCursor(result.nextCursor ?? '');
            setPage(targetPage);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Fresh query always resets paging to page 1.
        setCursorByPage({ 1: '' });
        setPage(1);
        loadProducts(1, queryValue, '');
    }, [queryValue, loadProducts]);

    useEffect(() => {
        const loadYears = async () => {
            setLoadingYears(true);
            try {
                const result = await agent.Vehicles.filterOptions();
                setYearOptions([
                    ...DEFAULT_YEAR_OPTIONS,
                    ...result.years.map((year) => ({ label: year, value: year })),
                ]);
            } catch {
                setYearOptions(DEFAULT_YEAR_OPTIONS);
            } finally {
                setLoadingYears(false);
            }
        };

        loadYears();
    }, []);

    useEffect(() => {
        if (!selectedYear) {
            setMakeOptions(DEFAULT_MAKE_OPTIONS);
            return;
        }

        const loadMakes = async () => {
            setLoadingMakes(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear);
                setMakeOptions([
                    ...DEFAULT_MAKE_OPTIONS,
                    ...result.makes.map((make) => ({ label: make, value: make })),
                ]);
            } catch {
                setMakeOptions(DEFAULT_MAKE_OPTIONS);
            } finally {
                setLoadingMakes(false);
            }
        };

        loadMakes();
    }, [selectedYear]);

    useEffect(() => {
        if (!selectedYear || !selectedMake) {
            setModelOptions(DEFAULT_MODEL_OPTIONS);
            return;
        }

        const loadModels = async () => {
            setLoadingModels(true);
            try {
                const result = await agent.Vehicles.filterOptions(selectedYear, selectedMake);
                setModelOptions([
                    ...DEFAULT_MODEL_OPTIONS,
                    ...result.models.map((model) => ({ label: model, value: model })),
                ]);
            } catch {
                setModelOptions(DEFAULT_MODEL_OPTIONS);
            } finally {
                setLoadingModels(false);
            }
        };

        loadModels();
    }, [selectedYear, selectedMake]);

    // Keep non-query filters client-side for this first realtime UI pass.
    const filteredProducts = useMemo(() => {
        const hasTagFilter = taggedWith.trim().length > 0;

        return products.filter((product) => {
            if (vendor && !(product.vendor || '').toLowerCase().includes(vendor.toLowerCase())) return false;
            if (hasFitment[0] === 'has' && product.fitmentCount < 1) return false;
            if (hasFitment[0] === 'none' && product.fitmentCount > 0) return false;
            if (hasTagFilter) return true; // UI parity only; realtime endpoint does not return tags yet.
            if (mappedOnly && product.fitmentCount < 1) return false;
            return true;
        });
    }, [products, vendor, hasFitment, taggedWith, mappedOnly]);

    const resourceProducts = filteredProducts.map((p) => ({ ...p, id: String(p.id) }));

    const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } =
        useIndexResourceState(resourceProducts);

    const selectedCount = allResourcesSelected ? resourceProducts.length : selectedResources.length;

    const onBulkFitmentAction = useCallback(async (action: 'add' | 'replace' | 'delete') => {
        if (selectedCount < 1) return;

        const requiresVehicleSelection = action === 'add' || action === 'replace';
        if (requiresVehicleSelection && (!selectedYear || !selectedMake || !selectedModel)) {
            showToast('Select year, make, and model before running this action');
            return;
        }

        try {
            await agent.Products.bulkFitments({
                action,
                productIds: allResourcesSelected ? null : selectedResources.map((id) => Number(id)),
                scope: allResourcesSelected
                    ? {
                        query: queryValue,
                        hasFitment: hasFitment[0] || '',
                        vendor,
                        tag: taggedWith,
                    }
                    : undefined,
                year: selectedYear,
                make: selectedMake,
                model: selectedModel,
            });

            showToast(
                action === 'add'
                    ? 'Fitments added'
                    : action === 'replace'
                        ? 'Fitments replaced'
                        : 'Fitments removed'
            );

            clearSelection();
            await loadProducts(page, queryValue, cursorByPage[page] ?? '');
        } catch (error: any) {
            showToast(error?.message ?? 'Failed to update fitments');
        }
    }, [
        selectedCount,
        selectedYear,
        selectedMake,
        selectedModel,
        allResourcesSelected,
        selectedResources,
        queryValue,
        hasFitment,
        vendor,
        taggedWith,
        clearSelection,
        loadProducts,
        page,
        cursorByPage,
    ]);

    const promotedBulkActions = [
        {
            content: 'Add fitments',
            onAction: () => onBulkFitmentAction('add'),
            disabled: selectedCount < 1,
        },
    ];

    const bulkActions = [
        {
            content: 'Replace fitments (clear and reassign)',
            onAction: () => onBulkFitmentAction('replace'),
            disabled: selectedCount < 1,
        },
        {
            icon: DeleteIcon,
            content: 'Remove fitments',
            destructive: true,
            onAction: () => onBulkFitmentAction('delete'),
            disabled: selectedCount < 1,
        },
    ];

    const handleHasFitmentRemove = useCallback(() => setHasFitment([]), []);
    const handleVendorRemove = useCallback(() => setVendor(''), []);
    const handleTaggedWithRemove = useCallback(() => setTaggedWith(''), []);
    const handleQueryClear = useCallback(() => setQueryValue(''), []);

    const filters = [
        {
            key: 'hasFitment',
            label: 'Has fitments',
            filter: (
                <ChoiceList
                    title="Has fitments"
                    titleHidden
                    choices={[
                        { label: 'Has fitments', value: 'has' },
                        { label: 'No fitments', value: 'none' },
                    ]}
                    selected={hasFitment}
                    onChange={setHasFitment}
                />
            ),
            shortcut: true,
        },
        {
            key: 'vendor',
            label: 'Vendor',
            filter: (
                <TextField
                    label="Vendor"
                    value={vendor}
                    onChange={setVendor}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
        {
            key: 'taggedWith',
            label: 'Tag',
            filter: (
                <TextField
                    label="Tag"
                    value={taggedWith}
                    onChange={setTaggedWith}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        },
    ];

    const appliedFilters: IndexFiltersProps['appliedFilters'] = [];
    if (hasFitment.length > 0) {
        appliedFilters.push({
            key: 'hasFitment',
            label: hasFitment[0] === 'has' ? 'Has fitments' : 'No fitments',
            onRemove: handleHasFitmentRemove,
        });
    }
    if (!isEmpty(vendor)) {
        appliedFilters.push({
            key: 'vendor',
            label: `Vendor is ${vendor}`,
            onRemove: handleVendorRemove,
        });
    }
    if (!isEmpty(taggedWith)) {
        appliedFilters.push({
            key: 'taggedWith',
            label: `Tagged with ${taggedWith}`,
            onRemove: handleTaggedWithRemove,
        });
    }

    const onHandleCancel = () => { };

    const hasVehicleContext = Boolean(selectedYear && selectedMake && selectedModel);

    const sortOptions: IndexFiltersProps['sortOptions'] = [
        { label: 'Product title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Product title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Fitments', value: 'fitments asc', directionLabel: 'Lowest to highest' },
        { label: 'Fitments', value: 'fitments desc', directionLabel: 'Highest to lowest' },
        { label: 'Updated', value: 'updated asc', directionLabel: 'Oldest first' },
        { label: 'Updated', value: 'updated desc', directionLabel: 'Newest first' },
    ];

    const sortedResourceProducts = useMemo(() => {
        const [key, direction] = (sortSelected[0] || 'title asc').split(' ');
        const sign = direction === 'desc' ? -1 : 1;

        return [...resourceProducts].sort((a, b) => {
            if (key === 'fitments') return (a.fitmentCount - b.fitmentCount) * sign;
            if (key === 'updated') return ((a.updatedAt || '').localeCompare(b.updatedAt || '')) * sign;
            return a.title.localeCompare(b.title) * sign;
        });
    }, [resourceProducts, sortSelected]);

    return (
        <Page fullWidth title="Products">
            <Card>
                <div style={{ display: 'flex', gap: 16, margin: '16px 10px' }}>
                    <div style={{ flex: 1 }}>
                        <Select label="Year" options={yearOptions} onChange={(value) => { setSelectedYear(value); setSelectedMake(''); setSelectedModel(''); setMappedOnly(false); }} value={selectedYear} disabled={loadingYears} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Select label="Make" options={makeOptions} onChange={(value) => { setSelectedMake(value); setSelectedModel(''); setMappedOnly(false); }} value={selectedMake} disabled={loadingYears || loadingMakes || !selectedYear} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Select label="Model" options={modelOptions} onChange={(value) => { setSelectedModel(value); setMappedOnly(false); }} value={selectedModel} disabled={loadingYears || loadingMakes || loadingModels || !selectedYear || !selectedMake} />
                    </div>
                </div>

                {hasVehicleContext && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 10px 16px' }}>
                        <span style={{ fontSize: 13, color: '#616161' }}>
                            Viewing: {selectedYear} {selectedMake} {selectedModel}
                        </span>
                        <span style={{ fontSize: 13, color: '#616161' }}>Mapped: —</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input
                                type="checkbox"
                                checked={mappedOnly}
                                disabled={!hasVehicleContext}
                                onChange={(event) => setMappedOnly(event.target.checked)}
                            />
                            Show mapped only
                        </label>
                    </div>
                )}
                {!hasVehicleContext && (
                    <div style={{ margin: '0 10px 16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8a8a8a' }}>
                            <input type="checkbox" checked={false} disabled />
                            Show mapped only
                        </label>
                    </div>
                )}

                <IndexFilters
                    sortOptions={sortOptions}
                    sortSelected={sortSelected}
                    onSort={setSortSelected}
                    loading={loading}
                    cancelAction={{ onAction: onHandleCancel, disabled: false, loading: false }}
                    tabs={tabs}
                    selected={selectedTab}
                    onSelect={setSelectedTab}
                    queryValue={queryValue}
                    queryPlaceholder="Search products"
                    onQueryChange={setQueryValue}
                    onQueryClear={handleQueryClear}
                    onClearAll={() => {
                        setHasFitment([]);
                        setVendor('');
                        setTaggedWith('');
                        setQueryValue('');
                    }}
                    filters={filters}
                    appliedFilters={appliedFilters}
                    mode={mode}
                    setMode={setMode}
                />

                <IndexTable
                    bulkActions={bulkActions}
                    promotedBulkActions={promotedBulkActions}
                    resourceName={{ singular: 'product', plural: 'products' }}
                    itemCount={sortedResourceProducts.length}
                    hasMoreItems={hasNext}
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    onSelectionChange={handleSelectionChange}
                    headings={[
                        { title: 'Product' },
                        { title: 'SKU' },
                        { title: 'Fitments' },
                        { title: '' },
                    ]}
                    pagination={{
                        hasNext,
                        hasPrevious: page > 1,
                        onNext: async () => {
                            if (!nextCursor) return;
                            setCursorByPage((prev) => ({ ...prev, [page + 1]: nextCursor }));
                            await loadProducts(page + 1, queryValue, nextCursor);
                        },
                        onPrevious: async () => {
                            await loadProducts(page - 1, queryValue, cursorByPage[page - 1] ?? '');
                        },
                    }}
                    loading={loading}
                >
                    {sortedResourceProducts.map((product, index) => (
                        <IndexTable.Row
                            id={product.id}
                            key={product.id}
                            position={index}
                            selected={allResourcesSelected ? true : selectedResources.includes(product.id)}
                            onClick={() => navigate(`/products/${product.id}`)}
                        >
                            <IndexTable.Cell>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: product.fitmentCount > 0 ? '3px solid #2f855a' : '3px solid transparent', paddingLeft: 8 }}>
                                    {product.featuredImage ? (
                                        <Thumbnail source={product.featuredImage} alt={product.title} size="small" />
                                    ) : (
                                        <Thumbnail source={ImageIcon} alt={product.title} size="small" />
                                    )}
                                    <span>{product.title}</span>
                                </div>
                            </IndexTable.Cell>
                            <IndexTable.Cell>{product.sku || '—'}</IndexTable.Cell>
                            <IndexTable.Cell>{product.fitmentCount}</IndexTable.Cell>
                            <IndexTable.Cell />
                        </IndexTable.Row>
                    ))}
                </IndexTable>
            </Card>
        </Page>
    );
};

function isEmpty(value: string | string[]): boolean {
    if (Array.isArray(value)) return value.length === 0;
    return value === '' || value == null;
}

export default ProductsRealtimePage;
